import { useMemo } from 'react';

import { useMembershipReferralInfoQuery, useMembershipReferralsQuery } from '@occc/graphql';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SearchFormProps } from '@occc-app/design-system';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useQuery } from '@occc-app/dataAccessLayer/utils';

import {
  PAGINATION_AFTER_PARAM,
  PAGINATION_BEFORE_PARAM,
  PAGINATION_PAGE_SIZE,
  SEARCH_PARAM,
} from './membership-referrals.constants';

type UseMembershipReferralsParams = {
  membershipId: string;
};
export const useMembershipReferrals = (params: UseMembershipReferralsParams) => {
  const { membershipId } = params;

  const [paginationInfo, onPageChange] = usePaginationParams({
    pageSize: PAGINATION_PAGE_SIZE,
    beforeParamName: PAGINATION_BEFORE_PARAM,
    afterParamName: PAGINATION_AFTER_PARAM,
  });
  const [search, setSearch] = useSearchParams({
    searchParamName: SEARCH_PARAM,
    beforeParamName: PAGINATION_BEFORE_PARAM,
    afterParamName: PAGINATION_AFTER_PARAM,
  });

  const {
    data: referralsData,
    loading: isReferralsLoading,
    error,
    paginationProps,
  } = usePaginatedQuery(
    useMembershipReferralsQuery,
    'memberships.edges[0].node.referrals.pageInfo',
    undefined, // please use paginationInfo instead
    {
      variables: {
        membershipId,
        filter: { search: search },
      },
    },
    paginationInfo,
    onPageChange,
  );

  const { data: referralInfoData, loading: isReferralInfoLoading } = useQuery(
    useMembershipReferralInfoQuery,
    { variables: { membershipId } },
  );

  const handleSearchSubmit: SearchFormProps['onSubmit'] = ({ search: value }) => {
    setSearch(value);
  };

  const referrals = useMemo(() => {
    const node = referralsData?.memberships?.edges[0]?.node;
    if (!node) return [];

    return node.referrals.edges?.map((e) => e.node) || [];
  }, [referralsData?.memberships?.edges]);

  const { referralCode, totalReferrals } = useMemo(() => {
    const node = referralInfoData?.memberships?.edges[0]?.node;
    if (!node) return { referralCode: '-', totalReferrals: 0 };
    return { referralCode: node.referralCode, totalReferrals: node.referrals.totalCount };
  }, [referralInfoData?.memberships?.edges]);

  return {
    isReferralsLoading,
    isReferralInfoLoading,
    error,
    referralCode,
    totalReferrals,
    referrals,
    paginationProps,
    search: {
      value: search,
      onSubmit: handleSearchSubmit,
    },
  };
};
